import { Injectable } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { BehaviorSubject, Observable } from 'rxjs';
import { QuestionBase } from 'core/components/forms/question-base';
import {
  DropdownQuestion,
  QuestionGroup,
  TextareaQuestion,
  TextboxQuestion,
  WysiwygQuestion
} from 'core/components/forms';
import { CoursesService } from 'core/services/courses.service';
import { LocalesService } from 'core/services/locales.service';
import { ProfileService } from 'core/services/profile.service';
import { AdminCoursesQuestionsService } from './admin-courses-questions.service';
import { TranslateService } from '@ngx-translate/core';

@Injectable({
  providedIn: 'root'
})
export class RootCoursesQuestionsService extends AdminCoursesQuestionsService {
  public form: UntypedFormGroup;
  public formData: any;
  public user: any;
  protected questionsSubject = new BehaviorSubject(null);

  constructor(
    private coursesService: CoursesService,
    private localesService: LocalesService,
    protected profileService: ProfileService,
    protected translateService: TranslateService
  ) {
    super(translateService, profileService);
    this.profileService.getLoggedUser$().subscribe((user) => (this.user = user));
  }

  public getFormQuestions(params: any = {}): Observable<QuestionBase<any>[]> {
    this.createQuestions(params);
    return this.getQuestionsObservable();
  }

  public setForm(form: UntypedFormGroup) {
    this.form = form;
  }

  public onDataChanged(newData: any = {}) {
    this.formData = newData;
  }

  public getFormData() {
    return null;
  }

  public getQuestionsObservable() {
    return this.questionsSubject.asObservable();
  }

  public createQuestions(params: any = {}) {
    const { course, metaLang = 'es' } = params;
    this.coursesService.getCourseMeta(course.id, metaLang).then((courseMeta) => {
      const questions = [
        this.getCourseConfigGroup(params),
        this.getQuestions(courseMeta.lang === metaLang ? courseMeta : { lang: metaLang }, params)
      ];
      this.questionsSubject.next(questions);
    });
  }

  public getQuestions(courseMeta: any = {}, params: any = {}): QuestionGroup {
    const courseMetaGroup = new QuestionGroup({
      key: 'courseMetaIds',
      title: 'courses.info',
      isSection: true,
      isDropdown: true,
      isOpenCollapsible: true,
      childQuestions: [
        new DropdownQuestion({
          key: 'lang',
          label: 'nouns.lang',
          class: 'col-1',
          value: courseMeta.lang,
          options: this.localesService.getAvailableLocaleOptions(['es', 'en', 'fr', 'de', 'pt', 'nl']),
          order: 1,
          eventHandlers: {
            onChange: (value) => {
              const newLang = value;
              this.onSelectedLangChanged(newLang, params);
            }
          }
        }),
        new TextboxQuestion({
          key: 'title',
          label: 'courses.meta.title',
          class: 'col-1',
          value: courseMeta.title,
          validators: { required: true },
          order: 2
        }),
        new TextboxQuestion({
          key: 'topic',
          label: 'courses.meta.topic',
          class: 'col-1',
          value: courseMeta.topic,
          validators: { required: true },
          order: 3
        }),
        new TextareaQuestion({
          key: 'description',
          label: 'courses.meta.description',
          class: 'col-1',
          value: courseMeta.description,
          validators: { required: true },
          // TODO Add maxLength validator
          order: 4
        }),
        new WysiwygQuestion({
          key: 'objectives',
          label: 'courses.meta.objectives',
          class: 'col-1',
          value: courseMeta.objectives,
          order: 5
        }),
        new WysiwygQuestion({
          key: 'contents',
          label: 'courses.meta.contents',
          class: 'col-1',
          value: courseMeta.contents,
          order: 6
        }),
        new WysiwygQuestion({
          key: 'aimedAt',
          label: 'courses.meta.aimedAt',
          class: 'col-1',
          value: courseMeta.aimedAt,
          order: 7
        })
      ]
        .filter((question) => question != null)
        .sort((a, b) => a.order - b.order)
    });

    return courseMetaGroup;
  }

  public onSelectedLangChanged(newLang: string, params: any = {}) {
    params.metaLang = newLang;
    this.createQuestions(params);
  }
}
